// AutoRechargeModal.jsx
import { useState, useEffect } from "react";
import { Modal } from "@mui/material";
import Button from "../../Button";
import InputText from "../../Inputs/Text";

import "./styles.css";
import { useDispatch, useSelector } from "react-redux";

import * as accountService from "../../../services/account";
import { updateAccount } from "../../../actions/account";

import AddCard from "./AddCard";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUB_KEY);

const AutoRechargeModal = ({ open, onClose }) => {
    const dispatch = useDispatch();
    const account = useSelector((state) => state.accounts.data);

    const [form, setForm] = useState({
        autoRechargeEnabled: false,
        autoRechargeThreshold: 5,
        autoRechargeAmount: 100,
        autoRechargeLimitAmount: 250,
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [loadingDisabled, setLoadingDisabled] = useState(false);
    const [cardSetupContext, setCardSetupContext] = useState(null);

    const handleChange = (field) => (e) => {
        const value = field === "autoRechargeEnabled" ? e.target.checked : +e.target.value;
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const validate = () => {
        const newErrors = {};
        if (form.autoRechargeEnabled) {
            if (form.autoRechargeThreshold < 5 || form.autoRechargeThreshold > 199_995)
                newErrors.autoRechargeThreshold = "Value must be between $5 and $199,995";

            if (form.autoRechargeAmount < 10 || form.autoRechargeAmount > 200_000)
                newErrors.autoRechargeAmount = "Value must be between $10 and $200,000";

            if (
                form.autoRechargeLimitAmount !== 0 &&
                (form.autoRechargeLimitAmount < 100 || form.autoRechargeLimitAmount > 200_000)
            )
                newErrors.autoRechargeLimitAmount = "Value must be between $100 and $200,000";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        setLoading(true);
        if (!validate()) {
            setLoading(false);
            return;
        }

        let paymentMethodId = undefined;
        try {
            if (cardSetupContext?.clientSecret && !account.defaultPaymentMethodId) {
                const result = await cardSetupContext.stripe.confirmCardSetup(
                    cardSetupContext.clientSecret,
                    {
                        payment_method: {
                            card: cardSetupContext.cardElement,
                        },
                    }
                );

                if (result.error) {
                    throw new Error(result.error.message);
                }

                paymentMethodId = result.setupIntent.payment_method;
            }

            const converted = {
                ...account,
                autoRechargeEnabled: true,
                autoRechargeThreshold: Math.round(form.autoRechargeThreshold * 100),
                autoRechargeAmount: Math.round(form.autoRechargeAmount * 100),
                autoRechargeLimitAmount: Math.round(form.autoRechargeLimitAmount * 100),
                defaultPaymentMethodId: paymentMethodId
            };
            
            const response = await accountService.updateAccount(converted);
            
            if (response) {
                dispatch(updateAccount(response));
                onClose();
            }
        } catch (err) {
            console.error(err);
            alert("Erro ao salvar: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDisable = async () => {
        setLoadingDisabled(true);
        const converted = { ...account, autoRechargeEnabled: false };
        
        const response = await accountService.updateAccount(converted);
        
        if (response) {
            dispatch(updateAccount(response));
            onClose();
        }
        setLoadingDisabled(false);
    }

    useEffect(() => {
        setForm({
            autoRechargeEnabled: account.autoRechargeEnabled,
            autoRechargeThreshold: account.autoRechargeThreshold / 100,
            autoRechargeAmount: account.autoRechargeAmount / 100,
            autoRechargeLimitAmount: account.autoRechargeLimitAmount / 100,
        });
    }, [account.uuid, account.autoRechargeEnabled, account.autoRechargeThreshold, account.autoRechargeAmount, account.autoRechargeLimitAmount]);

    return (
        <Modal open={open} onClose={onClose}>
            <div className="auto-recharge-modal">
                <div className="auto-recharge-header">
                    <h2>Auto recharge settings</h2>
                    <p className="auto-recharge-description">
                        Configure automatic credit recharges to ensure your account always has sufficient balance for
                        transactions. You can set thresholds and amounts for recharges, as well as a monthly limit.
                    </p>
                </div>

                <div className="auto-recharge-body">
                    <InputText
                        label="When credit balance goes below"
                        type="number"
                        prefix="$"
                        value={form.autoRechargeThreshold}
                        onChange={handleChange("autoRechargeThreshold")}
                        error={!!errors.autoRechargeThreshold}
                        helperText={errors.autoRechargeThreshold || "Enter an amount between $5 and $199,995"}
                        disabled={!form.autoRechargeEnabled}
                    />

                    <InputText
                        label="Bring credit balance back up to"
                        type="number"
                        prefix="$"
                        value={form.autoRechargeAmount}
                        onChange={handleChange("autoRechargeAmount")}
                        error={!!errors.autoRechargeAmount}
                        helperText={errors.autoRechargeAmount || "Enter an amount between $10 and $200,000"}
                        disabled={!form.autoRechargeEnabled}
                    />

                    <InputText
                        label="Monthly auto‑recharge limit"
                        type="number"
                        prefix="$"
                        value={form.autoRechargeLimitAmount}
                        onChange={handleChange("autoRechargeLimitAmount")}
                        error={!!errors.autoRechargeLimitAmount}
                        helperText={
                            errors.autoRechargeLimitAmount ||
                            "Enter an amount between $100 and $200,000. Leave 0 for no limit."
                        }
                        disabled={!form.autoRechargeEnabled}
                    />

                    <Elements stripe={stripePromise} options={{ locale: "en" }}>
                        <AddCard onReady={(ctx) => setCardSetupContext(ctx)} />
                    </Elements>
                </div>

                <div className="auto-recharge-footer">
                    {account.autoRechargeEnabled && 
                        <Button 
                            kind="link" 
                            label="Disable auto recharge" 
                            loading={loadingDisabled}
                            onClick={handleDisable} 
                            style={{ 
                                display: "flex", 
                                justifyContent: "start",
                                padding: 0
                            }}
                        />
                    }
                    <Button
                        kind="primary"
                        loading={loading}
                        label={account.autoRechargeEnabled ? "Save changes" : "Enable auto recharge"}
                        onClick={handleSave}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default AutoRechargeModal;
